import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { DefaultValues, useForm } from "react-hook-form";
import { memo, useEffect } from "react";
import {
  writeRoleBasedProjectRates,
  WriteRoleBasedProjectRates,
} from "@/schemas/roleBasedProjectRates";
import Form from "@/components/Form";
import { RoleBasedProjectRatesService } from "@/services/roleBasedProjectRatesService";
import PageHeader from "@/components/PageHeader";
import { useProjectRateBreadcrumbs } from "@/modules/projects/components/rates/hooks/useProjectRateBreadcrumbs";
import { ProjectDetailsAndNavigation } from "@/modules/projects/components/ProjectDetailsAndNavigation";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import PageContent from "@/components/PageContent";
import { CircleDollarSign } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useProjectRateListQuery } from "@/modules/projects/components/rates/hooks/useProjectRateListQuery";
import { useToast } from "@/components/ui/use-toast";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { PATH_PROJECT_RATES } from "@/constants";
import { useProjectRateQuery } from "@/modules/projects/components/rates/hooks/useProjectRateQuery";
import ProjectRateEditorFooter from "@/modules/projects/components/rates/ProjectRateEditorFooter";
import ProjectRateFormFields from "@/modules/projects/components/rates/ProjectRateFormFields";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";

function ProjectRateEditorComponent() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const params = useParams();
  const rateId = params.rateId as string;
  const navigate = useNavigate();
  const projectId = useProjectId();
  const breadcrumbItems = useProjectRateBreadcrumbs();
  const { listQuery } = useProjectRateListQuery();
  const rateQuery = useProjectRateQuery();
  const { can_update } = useModulePermissions();
  const { mutateAsync: editProjectRate, isPending: editProjectRatePending } =
    useMutation({
      mutationFn: (values: WriteRoleBasedProjectRates) =>
        RoleBasedProjectRatesService.patch(rateId, values),
      onSuccess: () => {
        toast({
          title: t("projectRates.success.update"),
        });
        navigate(generatePath(PATH_PROJECT_RATES, { projectId }));
      },
      onError: () => {
        toast({
          title: t("projectRates.errors.update"),
          variant: "destructive",
        });
      },
    });

  const form = useForm<WriteRoleBasedProjectRates>({
    resolver: zodResolver(writeRoleBasedProjectRates),
    defaultValues: {
      project_id: projectId,
    } as DefaultValues<WriteRoleBasedProjectRates>,
    disabled:
      editProjectRatePending ||
      listQuery.isLoading ||
      rateQuery.isLoading ||
      !can_update,
  });

  const {
    mutateAsync: deleteProjectRate,
    isPending: deleteProjectRatePending,
  } = useMutation({
    mutationFn: () => RoleBasedProjectRatesService.delete(rateId),
    onSuccess: () => {
      toast({
        title: t("projectRates.success.delete"),
      });
      navigate(generatePath(PATH_PROJECT_RATES, { projectId }));
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: t("projectRates.errors.delete"),
      });
    },
  });

  useEffect(() => {
    if (rateQuery.data) {
      const currentValues = form.getValues();
      form.reset({
        ...currentValues,
        ...rateQuery.data,
      });
    }
  }, [rateQuery.data, form]);

  useFormUnsavedChangesWarning(form);
  const isPending = editProjectRatePending || deleteProjectRatePending;

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbItems} />
      <ProjectDetailsAndNavigation />

      <Form className="mt-2" form={form} handleSubmit={editProjectRate}>
        <PageContent
          icon={<CircleDollarSign className="w-6 h-6 mr-3" />}
          title={t("projectRates.title.edit")}
          footer={
            <ProjectRateEditorFooter
              onDelete={deleteProjectRate}
              isPending={isPending}
            />
          }
        >
          <ProjectRateFormFields
            isPending={isPending}
            projectRate={rateQuery.data}
          />
        </PageContent>
      </Form>
    </>
  );
}

export default memo(ProjectRateEditorComponent);
